/**
 * Helper Categories — per-society FV kinds catalog CRUD.
 *
 * Society admin curates the list of "frequent visitor" categories (maid, cook,
 * driver, … plus society-specific ones like ironwala, AC tech, etc.) and sets
 * the verification policy per kind:
 *
 *   verification_level     'recommended' or 'light' — drives the soft hint
 *                          shown on the FV add/edit dialog.
 *   hard_block_id_card     when true, POST /:id/id-card refuses to issue if
 *                          the FV's police_verification_status !== 'verified'.
 *
 * A category that's in use by an active FV CANNOT be hard-deleted (409) —
 * deactivate via the Active toggle instead.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit3, Trash2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';

type VerificationLevel = 'recommended' | 'light';

interface KindRow {
  id: string;
  code: string;
  label: string;
  verification_level: VerificationLevel;
  hard_block_id_card: boolean;
  issue_id_card: boolean;
  manual_capture_photo: boolean;
  ratings_enabled: boolean;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  items: KindRow[];
  next_cursor?: string | null;
}

const HELPER_CATEGORIES_PAGE_SIZE = 50;

const LEVEL_LABEL: Record<VerificationLevel, string> = {
  recommended: 'High-trust (police verification recommended)',
  light: 'Light-touch (photo + phone enough)'
};

const LEVEL_VARIANT: Record<VerificationLevel, 'warn' | 'muted'> = {
  recommended: 'warn',
  light: 'muted'
};

const CreateSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2, 'Code must be ≥2 chars')
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters / digits / underscore (e.g. "ironwala")'),
  label: z.string().trim().min(2, 'Label is required').max(80),
  verification_level: z.enum(['recommended', 'light']),
  hard_block_id_card: z.boolean(),
  issue_id_card: z.boolean(),
  manual_capture_photo: z.boolean(),
  ratings_enabled: z.boolean(),
  sort_order: z.coerce.number().int().min(0).max(10000),
  active: z.boolean()
});
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULT_FORM: CreateForm = {
  code: '',
  label: '',
  verification_level: 'light',
  hard_block_id_card: false,
  issue_id_card: true,
  manual_capture_photo: true,
  ratings_enabled: true,
  sort_order: 100,
  active: true
};

export function HelperCategoriesPage(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<KindRow | null>(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<KindRow | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  // Cursor pagination — no client-side filters yet, so the hash is static.
  // If filters are added later, fold them into this hash so accumulation
  // resets on filter change.
  const filtersHash = 'static';
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['fv-kinds', cursor],
    queryFn: () =>
      get<ListResponse>('/fv-kinds', {
        params: { limit: HELPER_CATEGORIES_PAGE_SIZE, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<KindRow>(listQuery.data, cursor, filtersHash);

  const items = paginator.items;

  const openCreate = (): void => {
    setEditing(null);
    form.reset(DEFAULT_FORM);
    setOpen(true);
  };
  const openEdit = (k: KindRow): void => {
    setEditing(k);
    form.reset({
      code: k.code,
      label: k.label,
      verification_level: k.verification_level,
      hard_block_id_card: k.hard_block_id_card,
      issue_id_card: k.issue_id_card,
      manual_capture_photo: k.manual_capture_photo,
      ratings_enabled: k.ratings_enabled,
      sort_order: k.sort_order,
      active: k.active
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async (v: CreateForm) => {
      if (editing) {
        // code is immutable post-create
        const { code: _drop, ...rest } = v;
        return patch<KindRow>(`/fv-kinds/${editing.id}`, rest);
      }
      return post<KindRow>('/fv-kinds', v);
    },
    onSuccess: () => {
      toast.success(editing ? 'Category updated' : 'Category added');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['fv-kinds'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<{ ok: true }>(`/fv-kinds/${id}`),
    onSuccess: () => {
      toast.success('Category deleted');
      setConfirmDeleteFor(null);
      void qc.invalidateQueries({ queryKey: ['fv-kinds'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmDeleteFor(null);
    }
  });

  const recommendedCount = items.filter((k) => k.verification_level === 'recommended').length;
  const hardBlockCount = items.filter((k) => k.hard_block_id_card).length;

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Helper categories"
        description="Curate the roles recognised at the gate (maid, cook, driver, ironwala, …) and set the police-verification policy per kind."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add category
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Total categories</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Active</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">
              {items.filter((k) => k.active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">High-trust</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{recommendedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Hard-blocked</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{hardBlockCount}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No categories yet"
              description="Add the helper roles your society recognises."
              action={
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> Add category
                </Button>
              }
            />
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Gate entry</TableHead>
                  <TableHead>Hard-block ID card</TableHead>
                  <TableHead>Ratings</TableHead>
                  <TableHead className="text-right">Sort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-mono text-vt-12">{k.code}</TableCell>
                    <TableCell className="font-medium text-vt-text">{k.label}</TableCell>
                    <TableCell>
                      <Badge variant={LEVEL_VARIANT[k.verification_level]}>
                        {k.verification_level === 'recommended' ? 'High-trust' : 'Light-touch'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {k.issue_id_card ? (
                        <Badge variant="info">QR ID card</Badge>
                      ) : k.manual_capture_photo ? (
                        <Badge variant="muted">Manual (photo + phone + ID)</Badge>
                      ) : (
                        <Badge variant="muted">Manual (phone + ID)</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {k.hard_block_id_card ? (
                        <span className="inline-flex items-center gap-1 text-vt-12 text-vt-alert">
                          <ShieldAlert className="h-3.5 w-3.5" /> On
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-vt-12 text-vt-text-muted">
                          <ShieldCheck className="h-3.5 w-3.5" /> Off
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={k.ratings_enabled ? 'ok' : 'muted'}>
                        {k.ratings_enabled ? 'On' : 'Off'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-vt-12 text-vt-text-muted">
                      {k.sort_order}
                    </TableCell>
                    <TableCell>
                      <Badge variant={k.active ? 'ok' : 'muted'}>
                        {k.active ? 'Active' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-vt-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(k)} title="Edit">
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDeleteFor(k)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-vt-4 pb-vt-3">
              <CursorPaginator
                hasMore={Boolean(paginator.nextCursor)}
                loading={listQuery.isFetching}
                itemsShown={items.length}
                pageSize={HELPER_CATEGORIES_PAGE_SIZE}
                onLoadMore={() => {
                  if (paginator.nextCursor) setCursor(paginator.nextCursor);
                }}
              />
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit category' : 'Add helper category'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update label, verification level, hard-block policy and ordering.'
                : 'Pick a short lowercase code (e.g. ironwala). It cannot be changed later.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="hc-code">Code</Label>
                <Input
                  id="hc-code"
                  placeholder="ironwala"
                  disabled={Boolean(editing)}
                  {...form.register('code')}
                />
                {form.formState.errors.code ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.code.message}</p>
                ) : null}
                {editing ? (
                  <p className="text-vt-11 text-vt-text-muted">
                    Code is immutable — existing FVs reference it.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="hc-label">Label</Label>
                <Input id="hc-label" placeholder="Ironwala" {...form.register('label')} />
                {form.formState.errors.label ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.label.message}</p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="hc-level">Verification level</Label>
              <Select id="hc-level" {...form.register('verification_level')}>
                {(['recommended', 'light'] as const).map((l) => (
                  <option key={l} value={l}>
                    {LEVEL_LABEL[l]}
                  </option>
                ))}
              </Select>
              <p className="text-vt-11 text-vt-text-muted">
                Shows a warn/muted hint on the FV add dialog.
              </p>
            </div>

            <label className="flex items-start gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <input
                type="checkbox"
                {...form.register('issue_id_card')}
                className="mt-0.5 h-4 w-4 accent-vt-primary"
              />
              <span className="flex-1 text-vt-13">
                <span className="font-semibold text-vt-text">Issue QR ID card for this kind</span>
                <span className="block text-vt-12 text-vt-text-muted">
                  Default ON: helper gets a printable QR card; gate scans the QR for entry.
                  Turn OFF for daily-delivery roles (milk, newspaper, …) where ops overhead of
                  100+ cards isn't worth it — gate guard then verifies the helper manually
                  (mode picked below).
                </span>
              </span>
            </label>

            {/* Sub-select: visible only when QR card is OFF */}
            {form.watch('issue_id_card') === false ? (
              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
                <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                  Manual gate entry mode
                </div>
                <label className="flex items-start gap-vt-2 cursor-pointer py-vt-1">
                  <input
                    type="radio"
                    value="photo"
                    checked={form.watch('manual_capture_photo') === true}
                    onChange={() => form.setValue('manual_capture_photo', true)}
                    className="mt-0.5 h-4 w-4 accent-vt-primary"
                  />
                  <span className="flex-1 text-vt-13">
                    <span className="font-semibold text-vt-text">Photo + phone + ID</span>
                    <span className="block text-vt-12 text-vt-text-muted">
                      Stricter. Guard captures a face photo at the gate AND verifies phone
                      match + paper ID before marking IN/OUT. Recommended for higher-trust
                      daily helpers.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-vt-2 cursor-pointer py-vt-1">
                  <input
                    type="radio"
                    value="no-photo"
                    checked={form.watch('manual_capture_photo') === false}
                    onChange={() => form.setValue('manual_capture_photo', false)}
                    className="mt-0.5 h-4 w-4 accent-vt-primary"
                  />
                  <span className="flex-1 text-vt-13">
                    <span className="font-semibold text-vt-text">Phone + ID</span>
                    <span className="block text-vt-12 text-vt-text-muted">
                      Lightest. Guard verifies phone match + paper ID only — no face capture.
                      Suitable for very low-risk recurring visitors (e.g. fixed daily
                      newspaper / milk man).
                    </span>
                  </span>
                </label>
              </div>
            ) : null}

            <label className="flex items-start gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <input
                type="checkbox"
                {...form.register('hard_block_id_card')}
                className="mt-0.5 h-4 w-4 accent-vt-primary"
              />
              <span className="flex-1 text-vt-13">
                <span className="font-semibold text-vt-text">Hard-block ID card issuance</span>
                <span className="block text-vt-12 text-vt-text-muted">
                  If on, the `POST /id-card` route will refuse for this kind unless police
                  verification is `verified`. Recommended for maid / cook / nanny in stricter
                  societies. No effect on already-issued cards. Ignored when "Issue QR ID card"
                  is off.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <input
                type="checkbox"
                {...form.register('ratings_enabled')}
                className="mt-0.5 h-4 w-4 accent-vt-primary"
              />
              <span className="flex-1 text-vt-13">
                <span className="font-semibold text-vt-text">Allow ratings &amp; reviews</span>
                <span className="block text-vt-12 text-vt-text-muted">
                  Default ON. Residents whose flat has an active+approved assignment with a
                  helper of this kind can leave a 1-5 star rating + optional review. Aggregate
                  score is visible on the helper's profile. Turn OFF for kinds where ratings
                  don't make sense (e.g. one-off delivery roles).
                </span>
              </span>
            </label>

            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="hc-sort">Sort order</Label>
                <Input
                  id="hc-sort"
                  type="number"
                  min={0}
                  max={10000}
                  {...form.register('sort_order')}
                />
                <p className="text-vt-11 text-vt-text-muted">
                  Lower = higher in pickers. Defaults: 10/20/30 for top kinds.
                </p>
              </div>
              <label className="mt-vt-5 flex items-center gap-vt-2 self-end text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('active')}
                  className="h-4 w-4 accent-vt-primary"
                />
                Active (visible in pickers)
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending || !form.formState.isValid}
              >
                {editing ? 'Save changes' : 'Add category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(confirmDeleteFor)}
        onOpenChange={(v) => (v ? null : setConfirmDeleteFor(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete category?</DialogTitle>
            <DialogDescription>
              {confirmDeleteFor ? (
                <>
                  Delete <span className="font-semibold">{confirmDeleteFor.label}</span> ({confirmDeleteFor.code})?
                  If any active FV still uses this code, the API will reject the delete — deactivate
                  it instead.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmDeleteFor(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              loadingText="Deleting…"
              onClick={() => confirmDeleteFor && remove.mutate(confirmDeleteFor.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
